import { useState, useEffect, useCallback } from 'react';
import { getApiSubscriptionUserIdProductParentId } from '../api/interconectar-api';
import { SubscriptionUsesViewModel } from '../api/models';
import { useAuth } from '../context/AuthContext';

interface RemainingCalculationsResponse {
  remaining: number;
  total: number;
  userSubscriptionId: string | null;
}

interface SubscriptionUsesResponse {
  results: SubscriptionUsesViewModel[];
  status: string;
  errors: string[];
  validationErrors: any[];
  hasMoreResults: boolean | null;
  continuationToken: string | null;
  successMessage: string;
}

export const useRemainingCalculations = (productId: string | null) => {
  const { state } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RemainingCalculationsResponse | null>(null);

  const fetchData = useCallback(async () => {
    if (!state?.isAuthenticated) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!productId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        throw new Error("User ID not found");
      }

      const response = await getApiSubscriptionUserIdProductParentId(userId, parseInt(productId)) as unknown as SubscriptionUsesResponse;
      if (response?.results && response.results.length > 0) {
        
        // Filtrar por odooProductParentId que coincida con el productId
        const filteredSubscriptions = response.results.filter(
          subscription => subscription.odooProductParentId === parseInt(productId)
        );
        
        // Filtrar por suscripciones que tengan remainingUses > 0
        const availableSubscriptions = filteredSubscriptions.filter(
          subscription => subscription.remainingUses && subscription.remainingUses > 0
        );
        
        if (availableSubscriptions.length > 0) {
          const subscription = availableSubscriptions[0];
          setData({
            remaining: subscription.remainingUses || 0,
            total: subscription.totalUses || 0,
            userSubscriptionId: subscription.userSubscriptionId || null
          });
        } else {
          throw new Error("No available subscription found for this product");
        }
      } else {
        throw new Error("No subscription data found");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productId, state?.isAuthenticated]);

  useEffect(() => {
    if (state?.isAuthenticated) {
      fetchData();
    } else {
      setData(null);
      setLoading(false);
    }
  }, [fetchData, state?.isAuthenticated]);

  return {
    loading: state?.isAuthenticated ? loading : false,
    error,
    data,
    refetch: fetchData
  };
}; 